import { useTranslation } from 'react-i18next';
import { formatPercent, formatTwoDecimals } from '../../Utils/decimalUtils';
import { toEditKey } from '../../Hooks/Price-simulation/useWorksheetAdjustments';
import { InfoLabel } from '../Badges/InfoLabel';
import NewPrice from './NewPrice';
import PriceDelta from './PriceDelta';
import type { CalculatedPlan, WorksheetInputEditor } from './PriceSimulationWorksheet.types';
import WithTooltip from '../Fundamentals/Tooltip/WithTooltip';
import WorksheetAdjustmentCell from './WorksheetAdjustmentCell';
import WorksheetBreakpointRow from './WorksheetBreakpointRow';
import WorksheetKeepPriceSlot from './WorksheetKeepPriceSlot';
import { isPlanHandled, revenueIncrease } from './worksheetCalculations';
import { deltaClass, formatMetric, formatQuantity, getStatusLabelType, signed, withSek } from './worksheetFormatters';
import { isTiered } from './priceSimulationModel';

interface WorksheetPlanRowProps {
    productId: string;
    plan: CalculatedPlan;
    editor: WorksheetInputEditor;
}

const WorksheetPlanRow = ({ productId, plan, editor }: WorksheetPlanRowProps) => {
    const { t } = useTranslation();

    const tiered = isTiered(plan);
    const isLocked = plan.status !== 'Draft';

    const increase = revenueIncrease(plan);

    return (
        <>
            <tr className="worksheet-plan-row">
                <td>
                    <div className="d-flex align-items-center gap-8 pl-32">
                        <svg className="flex-shrink-0 text-disabled" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                            <path d="M 2 0 L 2 8 L 10 8" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        </svg>
                        <div className="d-flex flex-column gap-4">
                            <div className="d-flex align-items-center gap-8">
                                <span className="text-secondary">{plan.name}</span>
                                {plan.isNew && (
                                    <WithTooltip content={t('priceSimulation.newPlanTooltip')} focusable>
                                        <InfoLabel type="secondary" title={t('priceSimulation.newPlan')} />
                                    </WithTooltip>
                                )}
                                {isLocked ? (
                                    <InfoLabel type={getStatusLabelType(plan.status)} title={plan.status} />
                                ) : isPlanHandled(plan) ? (
                                    <InfoLabel type="info" title={t('priceSimulation.modified')} />
                                ) : null}
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-6 body-xs text-secondary">
                                <span>
                                    {t('priceSimulation.revenue')}: {formatMetric(plan.revenue, withSek)}
                                </span>
                                {increase !== null && (
                                    <WithTooltip content={t('priceSimulation.revenueIncreaseTooltip')} focusable>
                                        <span className={`semibold ${deltaClass(increase)}`}>
                                            ({signed(increase)}
                                            {formatTwoDecimals(increase)})
                                        </span>
                                    </WithTooltip>
                                )}
                                <span aria-hidden="true">•</span>
                                <span>
                                    {t('priceSimulation.averageDiscount')}: {formatMetric(plan.averageDiscount, formatPercent)}
                                </span>
                                <span aria-hidden="true">•</span>
                                <WithTooltip content={t('priceSimulation.quantityTooltip')} focusable>
                                    <span>
                                        {t('priceSimulation.quantity')}: {formatMetric(plan.quantity, formatQuantity)}
                                    </span>
                                </WithTooltip>
                            </div>
                        </div>
                    </div>
                </td>
                <td className="text-right">{tiered ? null : withSek(plan.currentPrice ?? 0)}</td>
                <td className="text-center">
                    {tiered ? null : (
                        // Like included breakpoints, a 0 kr flat plan is kr-only: a percentage off 0 can never change the price.
                        <WorksheetAdjustmentCell
                            target={{ productId, planId: plan.id }}
                            currentPrice={plan.currentPrice ?? 0}
                            newPrice={plan.newListPrice ?? 0}
                            adjustment={plan.adjustment}
                            isKrOnly={(plan.currentPrice ?? 0) === 0}
                            isLocked={isLocked}
                            editor={editor}
                        />
                    )}
                </td>
                <td className="text-right">
                    {!tiered ? (
                        <div className="d-flex justify-content-between align-items-center gap-8">
                            <WorksheetKeepPriceSlot isKept={plan.isKept} onToggle={isLocked ? undefined : () => editor.onToggleKept({ productId, planId: plan.id }, plan)} />
                            <NewPrice price={plan.newListPrice ?? 0} delta={plan.delta} />
                        </div>
                    ) : plan.delta !== 0 ? (
                        <PriceDelta delta={plan.delta} />
                    ) : (
                        <span className="text-secondary">&mdash;</span>
                    )}
                </td>
            </tr>

            {tiered &&
                (plan.tierPoints ?? []).map((point, position) => (
                    <WorksheetBreakpointRow
                        key={toEditKey({ productId, planId: plan.id, breakpointIndex: point.index })}
                        productId={productId}
                        plan={plan}
                        point={point}
                        position={position}
                        isLocked={isLocked}
                        editor={editor}
                    />
                ))}
        </>
    );
};

export default WorksheetPlanRow;
